import { logger } from '@/lib';
import type { Dispatch, SetStateAction } from 'react';
import { returnToMerchant } from './returnToMerchantUtils';
import * as fpti from './fpti';

export type AuthEventName =
  | 'isPasskeyLoading'
  | 'challengeNotAvailable'
  | 'requestCanceled'
  | 'authProviderLoaded'
  | 'isAuthProviderLoading'
  | 'passkeyEnrollmentScreenShown'
  | 'publicKeyCreateTriggered'
  | 'publicKeyCreateSuccessful'
  | 'publicKeyCreateFailed'
  | 'publicKeyBindTriggered'
  | 'publicKeyBindSuccess'
  | 'prebindTriggered'
  | 'prebindSuccessful'
  | 'publicKeyBindFail';

export type AuthEvent = {
  event: AuthEventName;
  data?: {
    errorName: string;
    message: string;
  };
};

type Options = {
  isPasswordlessFlow: boolean;
  returnToMerchantUrl: string | null | undefined;
  setPasskeyError: Dispatch<SetStateAction<boolean>>;
};

export const authEventHandler = (event: AuthEvent, options: Options): void => {
  if (!event) {
    logger.error('passkey_iwc_event_undefined');
    return;
  }

  // setIsCtaHidden - can be used to control display of "add passkey" or "continue"
  // button on passkey enrollment screen of IWC
  // This is mainly used in error scenarios where we disable retrying
  // passkey enrollment based on current design
  const { isPasswordlessFlow, returnToMerchantUrl, setPasskeyError } = options;

  const { event: eventName, data } = event;
  const { errorName = '', message: errorMessage = '' } = data ?? {};

  logger.info(`passkey_iwc_event_triggered_${eventName}`);
  fpti.passkeyAuthEvent(eventName);

  const handleError = () => {
    fpti.passkeyAuthEvent(eventName, true);
    fpti.passkeyErrorShown({
      errorCode: errorName,
      errorDescription: errorMessage,
    });
    setPasskeyError(true);
    logger.error(`passkey_iwc_event_error_${eventName}`, {
      errorMessage,
      errorName,
      eventName,
    });
    logger.metric('GUEST_SIGNUP.PASSKEY.ERROR', {
      dimensions: {
        reason: eventName,
      },
    });
  };

  switch (eventName) {
    case 'isPasskeyLoading': // Button clicked
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'challengeNotAvailable':
      handleError();
      break;
    case 'requestCanceled': // User canceled
      handleError();
      break;
    case 'isAuthProviderLoading':
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'authProviderLoaded':
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'passkeyEnrollmentScreenShown':
      fpti.passkeyEnrollmentButtonShown();
      break;
    case 'publicKeyCreateTriggered':
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'publicKeyCreateSuccessful':
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'publicKeyCreateFailed':
      handleError();
      break;
    case 'publicKeyBindTriggered':
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'publicKeyBindSuccess': // Successfully added passkey
      fpti.passkeyAuthEvent(eventName);
      fpti.passkeyEnrollmentSuccess();
      logger.metric('GUEST_SIGNUP.PASSKEY.ENROLLMENT_SUCCESS');
      returnToMerchant({ isPasswordlessFlow, returnToMerchantUrl });
      break;
    case 'prebindTriggered':
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'prebindSuccessful':
      fpti.passkeyAuthEvent(eventName);
      break;
    case 'publicKeyBindFail':
      handleError();
      break;
    default:
      fpti.passkeyAuthEvent(eventName);
      logger.warn('passkey_iwc_event_unhandled', {
        errorMessage,
        errorName,
        eventName,
      });
      break;
  }
};
